import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';

import {
  activeGoalsAtom,
  archivedGoalsAtom,
  archiveGoalMutationAtom,
  createGoalMutationAtom,
  isLoadingAtom,
  MAX_ACTIVE_GOALS,
  sessionGoalsAppointmentIdAtom,
  sessionGoalsPatientIdAtom,
  updateGoalMutationAtom,
} from './atoms/sessionGoalsAtoms';
import { useGoalsAnalytics } from './hooks/useGoalsAnalytics';
import { SessionGoalsUI } from './SessionGoalsUI';
import type { SessionGoalsProps } from './types';

export const SessionGoals = ({
  patientId,
  appointmentId,
  showArchived = true,
  variant = 'default',
  stepNumber,
}: SessionGoalsProps) => {
  // Ref for intersection observer (view tracking)
  const widgetRef = useRef<HTMLDivElement>(null);

  // Set patient ID and appointment ID to trigger query
  const setPatientId = useSetAtom(sessionGoalsPatientIdAtom);
  const setAppointmentId = useSetAtom(sessionGoalsAppointmentIdAtom);

  useEffect(() => {
    setPatientId(patientId);
  }, [patientId, setPatientId]);

  useEffect(() => {
    setAppointmentId(appointmentId);
  }, [appointmentId, setAppointmentId]);

  // Read state
  const isLoading = useAtomValue(isLoadingAtom);
  const goals = useAtomValue(activeGoalsAtom);
  const archivedGoals = useAtomValue(archivedGoalsAtom);

  // Analytics tracking
  const {
    trackWidgetViewed,
    trackGoalAdded,
    trackGoalArchived,
    trackGoalEdited,
  } = useGoalsAnalytics(patientId, appointmentId);

  // Intersection Observer for view tracking - fires once when widget is 50% visible
  useEffect(() => {
    if (isLoading) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          trackWidgetViewed();
          observer.disconnect(); // Only fire once
        }
      },
      { threshold: 0.5 },
    );

    const widget = widgetRef.current;
    if (widget) {
      observer.observe(widget);
    }

    return () => observer.disconnect();
  }, [isLoading, trackWidgetViewed]);

  // Mutations
  const createMutation = useAtomValue(createGoalMutationAtom);
  const archiveMutation = useAtomValue(archiveGoalMutationAtom);
  const updateMutation = useAtomValue(updateGoalMutationAtom);

  const isMutating =
    createMutation.isPending ||
    archiveMutation.isPending ||
    updateMutation.isPending;

  // Handlers
  const handleCreateGoal = useCallback(
    async (content: string) => {
      const result = await createMutation.mutateAsync({
        content,
        patientId,
        appointmentId,
      });

      if (result?.id) {
        trackGoalAdded(result.id);
      }
    },
    [createMutation, patientId, appointmentId, trackGoalAdded],
  );

  const handleArchiveGoal = useCallback(
    async (goalId: string) => {
      await archiveMutation.mutateAsync({
        id: goalId,
        patientId,
      });
      trackGoalArchived(goalId);
    },
    [archiveMutation, patientId, trackGoalArchived],
  );

  const handleUpdateGoal = useCallback(
    async (goalId: string, content: string) => {
      await updateMutation.mutateAsync({
        id: goalId,
        content,
        patientId,
      });
      trackGoalEdited(goalId);
    },
    [updateMutation, patientId, trackGoalEdited],
  );

  return (
    <div ref={widgetRef}>
      <SessionGoalsUI
        isLoading={isLoading}
        goals={goals}
        archivedGoals={archivedGoals}
        maxGoals={MAX_ACTIVE_GOALS}
        isMutating={isMutating}
        onCreateGoal={handleCreateGoal}
        onArchiveGoal={handleArchiveGoal}
        onUpdateGoal={handleUpdateGoal}
        showArchived={showArchived}
        variant={variant}
        stepNumber={stepNumber}
      />
    </div>
  );
};
